import { useEffect, useState } from 'react'
import { TeamGroupSummary } from '@numeo/types-main-web-app'
import { useAppSelector } from 'hooks'
import { fetchManagedTeamGroups } from 'services/teamGroupsApi'

/**
 * The desks the current user MANAGES — the Dispatch Manager role, as the client sees it (ADR-027).
 *
 * Empty for everyone else, which is how a screen decides not to draw a manager-only control at all. The
 * server still gates every endpoint; this only decides what is worth rendering.
 *
 * `loaded` matters as much as the list: until the answer is in, "no groups" and "not a manager yet known"
 * look identical, and a route guard acting on the first would bounce a manager off their own page on every
 * refresh. Callers that gate navigation wait for it; callers that only hide a menu row need not.
 */
interface ManagedTeamGroups {
    groups: TeamGroupSummary[]
    loaded: boolean
}

/**
 * One request per identity, shared by every caller.
 *
 * `useBoxTruckAccess` is called from the sidebar, the route guards, Profiles and Fleet, so an un-cached fetch
 * inside it would fire a handful of identical requests on every navigation. The promise is cached, not the
 * value, so concurrent callers await the same in-flight request; a failure clears it so the next mount can
 * retry rather than remembering a blip for the whole session.
 *
 * KEYED BY WHO ASKED, which is not decoration. Logging out is a client-side navigation here — no page
 * reload — so a plain module cache would hand the next person to sign in on this tab the previous user's
 * desks, and a company switch would answer for the tenant they just left. The key changes, the answer is
 * fetched again.
 */
let cache: { key: string; at: number; pending: Promise<TeamGroupSummary[]> } | null = null

/**
 * How long an answer is reused before it is asked again.
 *
 * Membership is not part of the key and cannot be — it changes on the ADMIN's screen, not this one — so a
 * promotion or a demotion would otherwise sit unnoticed until the tab is reloaded. A minute bounds that:
 * the server is the real gate either way, and this only decides whether a menu row is drawn.
 */
const TTL_MS = 60_000

const load = (key: string, now: number): Promise<TeamGroupSummary[]> => {
    if (cache?.key !== key || now - cache.at > TTL_MS) {
        const pending = fetchManagedTeamGroups().catch((error) => {
            if (cache?.key === key) cache = null
            throw error
        })
        cache = { key, at: now, pending }
    }
    return cache.pending
}

export const useManagedTeamGroups = (enabled: boolean = true): ManagedTeamGroups => {
    // Identity, not just the user: switching company keeps the same person but changes which desks exist.
    const userId = useAppSelector((state) => state.auth.user?.id)
    const applicationId = useAppSelector((state) => state.auth.application?.id)
    const key = `${userId ?? ''}:${applicationId ?? ''}`

    // Disabled callers are `loaded` by definition: they already know the answer does not apply to them (an
    // admin sees everything regardless), so nothing should sit waiting on a request that will never run.
    const [state, setState] = useState<ManagedTeamGroups>({ groups: [], loaded: !enabled })

    useEffect(() => {
        if (!enabled || !userId) {
            setState({ groups: [], loaded: true })
            return
        }
        let alive = true
        setState((current) => (current.loaded ? { groups: [], loaded: false } : current))
        void load(key, Date.now())
            .then((groups) => alive && setState({ groups, loaded: true }))
            .catch((error) => {
                // Fail CLOSED, and say the answer arrived: the manager-only surfaces stay hidden rather than
                // appearing and then 403-ing, which is the same direction the server takes when it cannot
                // read the policy.
                //
                // LOUDLY, though. Hiding is indistinguishable from "you manage nothing" on screen, so a
                // sustained outage would take Analytics, the Groups tab and the editor away from every
                // manager with nothing anywhere to say why.
                console.error('[team-groups] failed to resolve managed groups — manager surfaces stay hidden', error)
                if (alive) setState({ groups: [], loaded: true })
            })
        return () => {
            alive = false
        }
    }, [enabled, key, userId])

    return state
}
